/**
 * Order-size limits (round 6). The customer-facing cap is TOTAL BYTES per order
 * (limits.maxOrderBytes, 5 GB); the photo count (limits.maxPhotosPerOrder,
 * 1000) is a distant safety rail. Pure planning helpers shared by the store's
 * addFiles gate and the upload screen's messaging — unit-tested.
 */

export interface OrderLimits {
  maxPhotosPerOrder: number;
  maxOrderBytes: number;
}

export interface AcceptPlan {
  /** Indexes (into the offered files array) that fit within both caps. */
  acceptedIndexes: number[];
  /** Files skipped because they would push the order past maxOrderBytes. */
  rejectedBytes: number;
  /** Files skipped because the photo-count safety rail was hit. */
  rejectedCount: number;
}

/**
 * Which of the offered files fit into the order, given what is already there.
 * Greedy in offer order: a file that would cross the byte cap is skipped, but
 * later smaller files may still fit. The count rail stops everything after it.
 */
export function planFileAcceptance(
  existing: { count: number; bytes: number },
  fileSizes: readonly number[],
  limits: OrderLimits,
): AcceptPlan {
  const plan: AcceptPlan = { acceptedIndexes: [], rejectedBytes: 0, rejectedCount: 0 };
  let count = existing.count;
  let bytes = existing.bytes;
  for (let i = 0; i < fileSizes.length; i++) {
    if (count >= limits.maxPhotosPerOrder) {
      plan.rejectedCount += 1;
      continue;
    }
    const size = fileSizes[i]!;
    if (bytes + size > limits.maxOrderBytes) {
      plan.rejectedBytes += 1;
      continue;
    }
    plan.acceptedIndexes.push(i);
    count += 1;
    bytes += size;
  }
  return plan;
}

/**
 * Duplicate-photo filter (metadata identity: filename + byte size). Splits the
 * offered files into fresh ones and duplicates of photos already in the order
 * (or of an earlier file in the same offer). Customers who want the same photo
 * on several papers configure that in Size & Paper — a second upload of the
 * identical file only clutters the grid. Shared by the store's addFiles gate
 * and the upload screen's messaging, like planFileAcceptance above.
 */
export function splitDuplicateFiles(
  existing: ReadonlyArray<{ filename: string; bytes: number }>,
  files: readonly File[],
): { fresh: File[]; duplicates: File[] } {
  const seen = new Set(existing.map((p) => `${p.filename} ${p.bytes}`));
  const fresh: File[] = [];
  const duplicates: File[] = [];
  for (const f of files) {
    const key = `${f.name} ${f.size}`;
    if (seen.has(key)) {
      duplicates.push(f);
    } else {
      seen.add(key);
      fresh.push(f);
    }
  }
  return { fresh, duplicates };
}

const GB = 1024 ** 3;

/** `1288490188` → `1.2` (one decimal, GB). Sub-0.05 GB shows as `0.0`. */
export function gbOf(bytes: number): string {
  return (bytes / GB).toFixed(1);
}

/** Whole-number cap label, e.g. `5 GB`. */
export function gbCapLabel(maxOrderBytes: number): string {
  const gb = maxOrderBytes / GB;
  return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
}

/** Header meter text: `1.2 GB of 5 GB`. */
export function orderBytesLabel(bytes: number, maxOrderBytes: number): string {
  return `${gbOf(bytes)} GB of ${gbCapLabel(maxOrderBytes)}`;
}
